import { z } from 'zod';
import { ReportReasonSchema } from '../reports';
import { AdminRoleSchema } from './roles';
import { AdminPageQuerySchema, adminPage } from './page';

/* ---------------- users list ---------------- */

export const AdminUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  handle: z.string().nullable(),
  onboarded: z.boolean(),
  createdAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime().nullable(),
  suspendedUntil: z.iso.datetime().nullable(),
  banned: z.boolean(),
  adminRole: AdminRoleSchema.nullable(),
  reportCount: z.number().int().nonnegative(),
  sanctionCount: z.number().int().nonnegative(),
  termsVersion: z.string().nullable(),
  termsAcceptedAt: z.iso.datetime().nullable(),
});
export type AdminUser = z.infer<typeof AdminUserSchema>;

export const AdminUserFilterSchema = z.enum([
  'all',
  'banned',
  'suspended',
  'reported',
  'admins',
  'not_onboarded',
]);
export type AdminUserFilter = z.infer<typeof AdminUserFilterSchema>;

export const AdminUsersQuerySchema = AdminPageQuerySchema.extend({
  q: z.string().max(120).optional(),
  filter: AdminUserFilterSchema.default('all'),
});
export type AdminUsersQuery = z.infer<typeof AdminUsersQuerySchema>;

export const AdminUsersResponseSchema = adminPage(AdminUserSchema);
export type AdminUsersResponse = z.infer<typeof AdminUsersResponseSchema>;

/* ---------------- user detail ---------------- */

/** A single entry in the account's enforcement history. */
export const SanctionEntrySchema = z.object({
  id: z.string(),
  action: z.string(),
  actorHandle: z.string(),
  reason: z.string(),
  createdAt: z.iso.datetime(),
});
export type SanctionEntry = z.infer<typeof SanctionEntrySchema>;

/** One report filed against this user (moderator view — snapshot only). */
export const UserReportEntrySchema = z.object({
  id: z.string(),
  reason: ReportReasonSchema,
  severity: z.enum(['low', 'medium', 'high']),
  status: z.enum(['open', 'actioned', 'dismissed']),
  snapshot: z.string().nullable(),
  createdAt: z.iso.datetime(),
});
export type UserReportEntry = z.infer<typeof UserReportEntrySchema>;

export const ModNoteSchema = z.object({
  id: z.string(),
  authorHandle: z.string(),
  body: z.string(),
  createdAt: z.iso.datetime(),
});
export type ModNote = z.infer<typeof ModNoteSchema>;

/**
 * The full moderator view of one account. Every field here is enumerated in the
 * Privacy Policy. **No Whisper / Circle message text** — activity is counts only.
 */
export const AdminUserDetailSchema = z.object({
  // account
  id: z.string(),
  email: z.string(),
  handle: z.string().nullable(),
  pronouns: z.string().nullable(),
  bio: z.string().nullable(),
  vibeEmoji: z.string().nullable(),
  flavor: z.string().nullable(),
  tags: z.array(z.string()),
  onboarded: z.boolean(),
  createdAt: z.iso.datetime(),
  lastSeenAt: z.iso.datetime().nullable(),
  lastLoginAt: z.iso.datetime().nullable(),
  lastLoginIp: z.string().nullable(),
  // standing
  banned: z.boolean(),
  suspendedUntil: z.iso.datetime().nullable(),
  rateMultiplier: z.number(),
  adminRole: AdminRoleSchema.nullable(),
  // terms
  termsVersion: z.string().nullable(),
  termsAcceptedAt: z.iso.datetime().nullable(),
  termsAcceptedIp: z.string().nullable(),
  // safety signals
  reportsAgainstCount: z.number().int().nonnegative(),
  reportsByCount: z.number().int().nonnegative(),
  distinctBlockers: z.number().int().nonnegative(),
  blocksInitiated: z.number().int().nonnegative(),
  reportsAgainst: z.array(UserReportEntrySchema),
  sanctions: z.array(SanctionEntrySchema),
  // activity metadata (no content)
  activeWhisperThreads: z.number().int().nonnegative(),
  circlesJoined: z.number().int().nonnegative(),
  sparksSent7d: z.number().int().nonnegative(),
  circlePosts24h: z.number().int().nonnegative(),
  currentCheckIn: z
    .object({ spotName: z.string(), glowOn: z.boolean(), since: z.iso.datetime() })
    .nullable(),
  // staff
  notes: z.array(ModNoteSchema),
});
export type AdminUserDetail = z.infer<typeof AdminUserDetailSchema>;

export const AdminUserDetailResponseSchema = z.object({ user: AdminUserDetailSchema });
export type AdminUserDetailResponse = z.infer<typeof AdminUserDetailResponseSchema>;

/* ---------------- actions ---------------- */

export const USER_ACTIONS = [
  'warn',
  'suspend',
  'suspend_30d',
  'lift_sanction',
  'set_rate_multiplier',
  'force_logout',
  'clear_profile_text',
  'ban',
  'unban',
  'force_handle_reset',
  'delete',
] as const;
export const UserActionSchema = z.enum(USER_ACTIONS);
export type UserActionName = z.infer<typeof UserActionSchema>;

export const AdminUserActionSchema = z
  .object({
    action: UserActionSchema,
    reason: z.string().min(3).max(500),
    /** for `suspend` */
    days: z.number().int().min(1).max(365).optional(),
    /** for `set_rate_multiplier` (1 = normal, higher = stricter) */
    multiplier: z.number().min(1).max(10).optional(),
  })
  .refine((v) => v.action !== 'suspend' || v.days != null, {
    message: 'suspend needs `days`',
    path: ['days'],
  })
  .refine((v) => v.action !== 'set_rate_multiplier' || v.multiplier != null, {
    message: 'set_rate_multiplier needs `multiplier`',
    path: ['multiplier'],
  });
export type AdminUserAction = z.infer<typeof AdminUserActionSchema>;

export const AddModNoteSchema = z.object({ body: z.string().min(1).max(2000) });
export type AddModNote = z.infer<typeof AddModNoteSchema>;
